/* src/components/settings/SettingSectionComponents.js */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Note: Specific icons for SectionCard titles will be passed as props,
// so no need to import them all here. faSlidersH was an example in the original file.

// Loading Spinner
export const LoadingSpinner = () => (
    <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
    >
        <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
        ></circle>
        <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
    </svg>
);

// Section Card (Updated to include icon support, matching AccountPage style)
export const SectionCard = ({ title, icon, children }) => (
    <div className="bg-white p-4 sm:p-6 border border-gray-200 rounded-lg shadow-sm mb-6 transition-shadow hover:shadow-md">
        <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            {icon && <FontAwesomeIcon icon={icon} className="mr-3 h-5 w-5 text-indigo-600" />}
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
        {children}
    </div>
);

// Key Binding Input (Updated with disabled prop)
export const KeyBindingInput = ({ label, currentKey, isSetting, onStartSetting, disabled = false }) => {
    const formatKeyDisplay = (key) => {
        if (!key) return 'None';
        if (key === ' ') return 'Spacebar';
        if (key === 'ArrowLeft') return '←';
        if (key === 'ArrowRight') return '→';
        if (key === 'ArrowUp') return '↑';
        if (key === 'ArrowDown') return '↓';
        if (key.length === 1) return key.toUpperCase();
        if (key.startsWith('Key')) return key.substring(3).toUpperCase();
        if (key.startsWith('Digit')) return key.substring(5);
        return key;
    };
    return (
        <div className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
            <span className="text-sm font-medium text-gray-700">{label}:</span>
            <button
                onClick={onStartSetting}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors min-w-[120px] text-center border ${
                    isSetting
                        ? 'bg-yellow-200 text-yellow-800 border-yellow-400 animate-pulse'
                        : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                aria-live="polite"
                aria-label={`${label}, Current key: ${formatKeyDisplay(currentKey)}, Click to change`}
                disabled={disabled}
            >
                 {isSetting ? 'Press a key...' : formatKeyDisplay(currentKey)}
            </button>
        </div>
    );
};

// Toggle Switch Component (Updated with disabled interaction)
export const ToggleSwitch = ({ label, description, checked, onChange, disabled = false }) => (
    <div className="flex items-center justify-between py-3">
        <span className="flex-grow flex flex-col mr-4">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            {description && <span className="text-xs text-gray-500 mt-0.5">{description}</span>}
        </span>
        <button
            type="button"
            onClick={() => !disabled && onChange(!checked)}
            className={`${
                checked ? 'bg-indigo-600' : 'bg-gray-200'
            } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            aria-label={label}
        >
            <span
                aria-hidden="true"
                className={`${
                    checked ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
            />
        </button>
    </div>
);

// Dropdown Input Component
export const DropdownInput = ({ label, id, value, onChange, options, disabled = false, description = null, className = "" }) => (
    <div className={`py-2 ${className}`}>
        <div className="flex items-center justify-between">
            <label htmlFor={id} className="text-sm font-medium text-gray-700 mr-4 flex-shrink-0">{label}:</label>
            <div className="relative flex-grow">
                <select
                    id={id}
                    name={id}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md disabled:bg-gray-100 disabled:text-gray-500 appearance-none bg-white shadow-sm"
                >
                    {options.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </div>
            </div>
        </div>
        {description && !disabled && <p className="text-xs text-gray-500 mt-1 text-right">{description}</p>}
    </div>
);

// Slider Input Component
export const SliderInput = ({ label, id, value, onChange, min, max, step, disabled = false, displayValue }) => (
    <div className="py-3">
        <div className="flex items-center justify-between mb-1">
             <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}:</label>
             <span className={`text-sm font-semibold ${disabled ? 'text-gray-400' : 'text-indigo-600'} tabular-nums`}>{displayValue}</span>
        </div>
        <input
            type="range"
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 accent-indigo-600"
        />
    </div>
);